import {
  getEditorActionMeta,
  isEndpointShapedActionId,
  scopeLevelSatisfies,
} from "@/constants/editorActionPolicy"
import {
  hasAuthoritativeAccessProfile,
  hasAnyModuleAccess,
  resolveBroadModuleAccessLevel,
  resolveModuleAccessLevel,
  resolveModuleCapabilityDecision,
} from "@/security/moduleAccessAuthority"

/**
 * Shared Editor action-policy authority composable.
 *
 * @precondition options.accessProfile is either nullish or backend-derived effective profile.
 * @precondition options.moduleId is current module id (number|string) when scope-based checks are needed.
 * @postcondition Exposes stable `{ can, uiState, actionMeta }` API for UI/security consumers.
 * @mutates none
 */
export function useEditorActionPolicy(options = {}) {
  const {
    accessProfile = null,
    legacyPermissions: suppliedLegacyPermissions,
    modules: suppliedModules,
    moduleId = null,
  } = options
  const legacyPermissions =
    suppliedLegacyPermissions ??
    options.userRolePermissions ??
    options.permissions ??
    {}
  const modules =
    suppliedModules ??
    options.moduleInventory ??
    (moduleId == null ? [] : [{ module_id: moduleId }])

  const actionMeta = (actionId) => {
    const endpointShaped = isEndpointShapedActionId(actionId)
    if (endpointShaped) {
      return {
        id: typeof actionId === "string" ? actionId : String(actionId ?? ""),
        valid: false,
        reason: "endpoint_shaped_action_id",
      }
    }

    const meta = getEditorActionMeta(actionId)
    if (!meta) {
      return {
        id: typeof actionId === "string" ? actionId : String(actionId ?? ""),
        valid: false,
        reason: "unknown_action_id",
      }
    }

    return {
      ...meta,
      valid: true,
    }
  }

  const can = (actionId, context = {}) => {
    const meta = actionMeta(actionId)
    if (!meta.valid) return false

    const authoritativeProfile = hasAuthoritativeAccessProfile(accessProfile)
    if (accessProfile?.is_superadmin) return true

    if (meta.aggregate === "modules") {
      return hasAnyModuleAccess(accessProfile, legacyPermissions)
    }

    if (meta.scopeType === "modules") {
      const requiredScopeLevel = meta.minScopeLevel || "view"
      const actionLegacyPermissions =
        context.legacyPermissions ??
        context.userRolePermissions ??
        legacyPermissions
      const actionModules =
        context.modules ?? context.moduleInventory ?? modules
      const actionModuleId = context.moduleId ?? moduleId
      const currentModuleScope = resolveModuleAccessLevel(
        accessProfile,
        actionModuleId,
        actionModules,
        actionLegacyPermissions
      )
      if (!scopeLevelSatisfies(currentModuleScope, requiredScopeLevel)) {
        return false
      }
      if (meta.scopeOnly === true) return true

      const legacyFallbackCapability =
        meta.legacyFallbackCapability ?? meta.fallbackCapability

      if (!authoritativeProfile) {
        return (
          meta.capability === "modules.view" ||
          legacyFallbackCapability === "modules.view"
        )
      }

      // A concrete module scope at view/edit is sufficient for the module-view
      // action. Named actions such as export, delete or prices continue
      // through their own capability decision below.
      if (meta.capability === "modules.view") return true

      const moduleCapability = meta.moduleCapability || meta.capability
      const capabilityDecision = resolveModuleCapabilityDecision(
        accessProfile,
        actionModuleId,
        moduleCapability,
        meta.fallbackCapability || null
      )
      if (capabilityDecision !== null) return capabilityDecision

      // Delete is historically inherited from module edit when the backend has
      // not emitted a separate catalog.delete decision. Other canonical actions
      // fail closed when their capability is absent.
      return meta.fallbackCapability === "modules.view"
        ? meta.capability === "catalog.delete"
        : false
    }

    if (meta.capability === "modules.view") {
      return scopeLevelSatisfies(
        resolveBroadModuleAccessLevel(accessProfile, legacyPermissions),
        "view"
      )
    }

    if (!authoritativeProfile) return false

    if (meta.reportAccess === true) {
      const reportKey = context.report || context.reportName || context.reportId
      if (reportKey) {
        if (Array.isArray(accessProfile.reports)) {
          return accessProfile.reports.includes(reportKey)
        }
        return accessProfile?.reports?.[reportKey] === true
      }
      if (
        Array.isArray(accessProfile.reports) &&
        accessProfile.reports.some(
          (reportName) => typeof reportName === "string" && reportName !== ""
        )
      ) {
        return true
      }
    }

    if (
      meta.capability &&
      accessProfile?.capabilities?.[meta.capability] !== true
    ) {
      return false
    }

    return true
  }

  const uiState = (actionId, context = {}) => {
    const allowed = can(actionId, context)
    const meta = actionMeta(actionId)
    const isMutation = meta.valid ? meta.mutation === true : true

    if (allowed) {
      return {
        enabled: true,
        hidden: false,
        state: "allowed",
      }
    }

    return {
      enabled: false,
      hidden: isMutation,
      state: isMutation ? "hidden" : "readonly",
    }
  }

  return {
    can,
    uiState,
    actionMeta,
  }
}

export default useEditorActionPolicy
